// Design System: gerenciamento de overrides de tokens CSS.
//
// Arquitetura:
// - Backend: GET/PUT /v1/system/theme (config GLOBAL, nao por cliente).
// - Frontend: aplica overrides ao :root via inline style.
// - Cache local: localStorage (apenas para evitar flash no primeiro paint;
//   a fonte da verdade e o backend).
// - Color scale: ao mudar `--brand-goiaba-500` (cor principal), todos os tons
//   da familia sao derivados automaticamente preservando a curva de
//   saturacao e luminancia da escala original. `--brand-purple-*` e um
//   alias legado pra --brand-navy-* (ver tokens.css) - nao e tocado aqui.

import { request } from './api-client';

const CACHE_KEY = 'just-frotas:theme-cache:v2';

/** Map de token CSS -> valor. Ex.: { '--color-primary': '#9834f7' } */
export type ThemeOverrides = Record<string, string>;

/** Config completa do tema (espelha shape do backend). */
export interface ThemeConfig {
    overrides: ThemeOverrides;
    logoUrl: string | null;
}

const EMPTY_CONFIG: ThemeConfig = { overrides: {}, logoUrl: null };

/** Categorias agrupadas para a UI do editor. */
export interface TokenSpec {
    token: string;
    label: string;
    kind: 'color' | 'font' | 'color-primary';
    fontOptions?: string[];
    hint?: string;
}

export interface TokenGroup {
    title: string;
    description?: string;
    tokens: TokenSpec[];
}

/** Tokens expostos no editor. Ordem importa visualmente. */
export const TOKEN_GROUPS: TokenGroup[] = [
    {
        title: 'Marca',
        description:
            'A cor primária (goiaba) é usada em CTAs, links e itens ativos. ' +
            'O navy define a sidebar e o hero. Ajuste apenas o tom 500 e a paleta inteira deriva.',
        tokens: [
            {
                token: '--brand-goiaba-500',
                label: 'Cor de ação (CTAs / itens ativos)',
                kind: 'color-primary',
                hint: 'Aplicada em botões principais, links, focus, badges e indicadores de seleção.',
            },
            {
                token: '--brand-navy-900',
                label: 'Sidebar (fundo)',
                kind: 'color',
                hint: 'Cor institucional escura aplicada na sidebar e no hero do login.',
            },
            {
                token: '--brand-goiaba-400',
                label: 'Logo (cor de texto fallback)',
                kind: 'color',
                hint: 'Usado quando não há imagem de logo configurada (sidebar collapsed).',
            },
        ],
    },
    {
        title: 'Cores semânticas',
        description: 'Pills de status, banners, feedback.',
        tokens: [
            { token: '--color-success', label: 'Sucesso', kind: 'color', hint: 'Aprovado, ativo, OK.' },
            { token: '--color-warning', label: 'Atenção', kind: 'color', hint: 'Pendente, em vigor.' },
            { token: '--color-danger', label: 'Erro / Perigo', kind: 'color', hint: 'Bloqueado, recusado, destrutivo.' },
            { token: '--color-info', label: 'Info', kind: 'color', hint: 'Informativo neutro.' },
        ],
    },
    {
        title: 'Superfícies e texto',
        tokens: [
            { token: '--color-bg', label: 'Fundo da página', kind: 'color' },
            { token: '--color-surface', label: 'Cards e modais', kind: 'color' },
            { token: '--color-text', label: 'Texto principal', kind: 'color' },
            { token: '--color-text-muted', label: 'Texto secundário', kind: 'color' },
        ],
    },
    {
        title: 'Tipografia',
        description: 'Famílias usadas em interface, valores e código.',
        tokens: [
            {
                token: '--font-sans',
                label: 'Fonte principal',
                kind: 'font',
                fontOptions: [
                    "'Inter', system-ui, -apple-system, sans-serif",
                    "'DM Sans', system-ui, sans-serif",
                    "'Plus Jakarta Sans', system-ui, sans-serif",
                    "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
                ],
                hint: 'Aplicada em todo o body.',
            },
            {
                token: '--font-display',
                label: 'Fonte de destaque',
                kind: 'font',
                fontOptions: [
                    "'DM Sans', 'Inter', system-ui, sans-serif",
                    "'Inter', system-ui, sans-serif",
                    "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
                    "'Space Grotesk', 'Inter', system-ui, sans-serif",
                ],
                hint: 'Nome de motorista no header, valores de KPI.',
            },
        ],
    },
];

/** Lista plana de todos os tokens expostos. */
export const ALL_TOKENS: TokenSpec[] = TOKEN_GROUPS.flatMap((g) => g.tokens);

// =============================================================
// Cache local (otimizacao de paint inicial)
// =============================================================

export function getCachedConfig(): ThemeConfig {
    if (typeof window === 'undefined' || !window.localStorage) return EMPTY_CONFIG;
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return EMPTY_CONFIG;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return EMPTY_CONFIG;
        return {
            overrides: parsed.overrides && typeof parsed.overrides === 'object' ? parsed.overrides : {},
            logoUrl: typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null,
        };
    } catch {
        return EMPTY_CONFIG;
    }
}

function setCachedConfig(config: ThemeConfig): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(config));
    } catch {
        // Storage cheio: ignora
    }
}

// =============================================================
// API
// =============================================================

export async function fetchThemeFromApi(): Promise<ThemeConfig> {
    const data = await request<ThemeConfig>('/system/theme', { method: 'GET' });
    return {
        overrides: data.overrides || {},
        logoUrl: data.logoUrl ?? null,
    };
}

export async function saveThemeToApi(config: ThemeConfig): Promise<ThemeConfig> {
    const data = await request<ThemeConfig>('/system/theme', {
        method: 'PUT',
        body: { overrides: config.overrides, logoUrl: config.logoUrl },
    });
    setCachedConfig(data);
    return data;
}

// =============================================================
// Aplicacao em :root
// =============================================================

/**
 * Converte hex para triple "r g b" (formato usado em --token-rgb pelo sistema
 * de tokens, necessario para rgba() com alpha channel - ex: --color-primary-soft).
 */
function hexToRgbTriple(hex: string): string | null {
    const h = computedToHex(hex);
    if (!h || !/^#[0-9a-f]{6}$/i.test(h)) return null;
    const r = parseInt(h.slice(1, 3), 16);
    const g = parseInt(h.slice(3, 5), 16);
    const b = parseInt(h.slice(5, 7), 16);
    return `${r} ${g} ${b}`;
}

/**
 * Aplica overrides em :root. Para cada token cujo valor e hex, tambem
 * sobrescreve a versao --token-rgb (triple "r g b") - necessario porque
 * tokens.css define muitos derivados via `rgb(var(--token-rgb))` e
 * `rgb(var(--token-rgb) / 0.15)` para alpha. Sem isso, sobrescrever apenas
 * --brand-goiaba-500 mantem --color-primary com o valor antigo.
 */
export function applyConfig(config: ThemeConfig): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // Defesa em camadas (review pos-dev Onda 1, LOW4): backend ja regex-valida
    // a chave (`^--[a-z0-9-]{1,64}$`) e o valor (CSS values sem url()/expression()).
    // Mas frontend nao deve confiar 100% em payload da rede. Validamos que a
    // chave esta dentro do conjunto de tokens conhecidos do design system,
    // ignorando silenciosamente qualquer outra chave.
    // --brand-purple-* de proposito NAO esta na allowlist: e alias legado
    // pra navy (tokens.css), nunca deve ser escrito por config de tema. Se
    // um config salvo antigo (pre-fix) ainda tiver essa chave, ela e
    // ignorada aqui - autocura na proxima carga, sem precisar migrar o DB.
    const allowedTokens = new Set<string>([
        ...ALL_TOKENS.map((t) => t.token),
        ...PURPLE_SCALE_STEPS.map((s) => `--brand-goiaba-${s}`),
    ]);
    for (const [token, value] of Object.entries(config.overrides || {})) {
        if (!allowedTokens.has(token)) continue;
        if (typeof value !== 'string') continue;
        root.style.setProperty(token, value);
        const rgbTriple = hexToRgbTriple(value);
        if (rgbTriple) {
            root.style.setProperty(`${token}-rgb`, rgbTriple);
        }
    }
}

export function clearTokenOverride(token: string): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.removeProperty(token);
    root.style.removeProperty(`${token}-rgb`);
}

export function clearAllOverrides(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // Limpa TODOS os tokens conhecidos + a escala goiaba derivada (com seus
    // -rgb). Inclui --brand-purple-* so defensivamente (nunca deveria ter
    // sido escrito - ver applyConfig - mas uma sessao aberta desde antes
    // deste fix pode ter inline style residual; remover e no-op se nao
    // existir).
    const all = [
        ...ALL_TOKENS.map((t) => t.token),
        ...PURPLE_SCALE_STEPS.map((s) => `--brand-purple-${s}`),
        ...PURPLE_SCALE_STEPS.map((s) => `--brand-goiaba-${s}`),
    ];
    for (const tok of all) {
        root.style.removeProperty(tok);
        root.style.removeProperty(`${tok}-rgb`);
    }
}

export function readComputedToken(token: string): string {
    if (typeof window === 'undefined') return '';
    return getComputedStyle(document.documentElement).getPropertyValue(token).trim();
}

// =============================================================
// Conversao de cor: hex <-> hsl
// =============================================================

export function computedToHex(value: string): string {
    if (!value) return '';
    const v = value.trim();
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) {
        if (v.length === 4) {
            return '#' + v.slice(1).split('').map((c) => c + c).join('').toLowerCase();
        }
        return v.toLowerCase();
    }
    if (typeof document === 'undefined') return '';
    try {
        const ctx = document.createElement('canvas').getContext('2d');
        if (!ctx) return '';
        ctx.fillStyle = '#000';
        ctx.fillStyle = v;
        const parsed = ctx.fillStyle as string;
        if (parsed.startsWith('#')) return parsed.toLowerCase();
        const m = parsed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (m) {
            const r = Number(m[1]).toString(16).padStart(2, '0');
            const g = Number(m[2]).toString(16).padStart(2, '0');
            const b = Number(m[3]).toString(16).padStart(2, '0');
            return ('#' + r + g + b).toLowerCase();
        }
    } catch {
        // ignora
    }
    return '';
}

interface Hsl { h: number; s: number; l: number }

function hexToHsl(hex: string): Hsl {
    const h = computedToHex(hex);
    if (!h) return { h: 0, s: 0, l: 0 };
    const r = parseInt(h.slice(1, 3), 16) / 255;
    const g = parseInt(h.slice(3, 5), 16) / 255;
    const b = parseInt(h.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let hh = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
            case g: hh = (b - r) / d + 2; break;
            case b: hh = (r - g) / d + 4; break;
        }
        hh /= 6;
    }
    return { h: hh * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
    const sn = clamp(s, 0, 100) / 100;
    const ln = clamp(l, 0, 100) / 100;
    const c = (1 - Math.abs(2 * ln - 1)) * sn;
    const hp = ((h % 360) + 360) % 360 / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0, g = 0, b = 0;
    if (hp < 1) { r = c; g = x; b = 0; }
    else if (hp < 2) { r = x; g = c; b = 0; }
    else if (hp < 3) { r = 0; g = c; b = x; }
    else if (hp < 4) { r = 0; g = x; b = c; }
    else if (hp < 5) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    const m = ln - c / 2;
    const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
    return ('#' + toHex(r) + toHex(g) + toHex(b)).toLowerCase();
}

function clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
}

// =============================================================
// Color scale: deriva paleta roxa a partir de uma cor base
// =============================================================

/**
 * Curva de saturacao/luminancia da escala roxa atual (calibrada visualmente).
 * Indexada por step (50, 100, ..., 1000). Apenas o HUE muda quando o usuario
 * escolhe uma cor diferente. Saturacao e luminancia preservam a "personalidade"
 * da escala original (clara nos tops, escura nas pontas).
 */
const PURPLE_SCALE_HSL: Record<string, { s: number; l: number }> = {
    '50':   { s: 100, l: 96 },
    '100':  { s: 100, l: 92 },
    '200':  { s: 100, l: 84 },
    '300':  { s: 100, l: 78 },
    '400':  { s: 100, l: 64 },
    '500':  { s: 92,  l: 58 },
    '600':  { s: 86,  l: 49 },
    '700':  { s: 84,  l: 40 },
    '800':  { s: 86,  l: 31 },
    '900':  { s: 98,  l: 21 },
    '1000': { s: 100, l: 9 },
};

export const PURPLE_SCALE_STEPS = Object.keys(PURPLE_SCALE_HSL);

/**
 * Determina a cor de texto (branco ou quase-preto) que deve ir SOBRE uma
 * cor de fundo, baseado na luminancia relativa W3C. Retorna o triple
 * "r g b" para uso em --color-on-primary-rgb (que tokens.css consome via
 * `rgb(var(--color-on-primary-rgb))`).
 *
 * Threshold 0.5 e empirico - funciona bem pra paletas vibrantes. Para
 * AAA strict, usar contrast ratio formal (4.5:1 sobre o bg).
 */
export function pickContrastTextRgb(bgHex: string): string {
    const h = computedToHex(bgHex);
    if (!h) return '255 255 255';
    const r = parseInt(h.slice(1, 3), 16) / 255;
    const g = parseInt(h.slice(3, 5), 16) / 255;
    const b = parseInt(h.slice(5, 7), 16) / 255;
    const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    // Luminance > 0.55: bg claro -> texto escuro. Senao: bg escuro -> branco.
    return lum > 0.55 ? '17 24 39' : '255 255 255';
}

/**
 * Deriva a paleta de marca completa a partir de uma cor base (geralmente o 500).
 * Estrategia: extrai apenas o HUE da cor escolhida e aplica a curva
 * pre-calibrada de S/L para cada step. Resultado: paleta consistente que
 * reflete a personalidade da cor escolhida sem brigar com a curva visual.
 *
 * Escreve APENAS em --brand-goiaba-* (a "cor de acao" viva - CTAs, itens
 * ativos). --brand-purple-* NAO e tocado aqui: e um alias LEGADO que
 * tokens.css aponta pra --brand-navy-* (nao pro accent - ver tokens.css
 * secao "LEGACY"), consumido por elementos que esperam um tom navy
 * institucional (label do 2FA em LoginPage.tsx, hover em RestricoesTab.tsx,
 * scrollbar dark do sidebar). Escrever a escala derivada da cor de acao ali
 * corrompia esses elementos com o hue errado - bug ja corrigido uma vez
 * (ver docs/contracts/DESIGN-SYSTEM.md); nao reintroduzir.
 */
export function derivePurpleScale(baseHex: string): ThemeOverrides {
    const { h } = hexToHsl(baseHex);
    const out: ThemeOverrides = {};
    for (const [step, { s, l }] of Object.entries(PURPLE_SCALE_HSL)) {
        out[`--brand-goiaba-${step}`] = hslToHex(h, s, l);
    }
    return out;
}
